import { useMemo } from 'react'

import i18n from 'i18n-literally'
import memoizeOne from 'memoize-one'
import { pick } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { Typography } from '@mui/material'

import {
  getBattery,
  getBattery9V,
  getSignalIcon,
  getStatus,
  rssiToSignal,
} from '~/src/Device/utils'
import {
  EMPTY_OBJECT,
  formattedDate,
  getDateTime,
  getShortDateDiff,
  getTimeDiffHours,
} from '~/src/Lib/Utils'

export const getBatteryPercentageWithColor = value => {
  if (value >= 30) {
    return <Typography color="primary">{value}%</Typography>
  }
  if (value >= 20) {
    return <Typography color="warning.main">{value}%</Typography>
  }
  return <Typography color="error">{value}%</Typography>
}

export const getSignalPercentageWithColor = value => {
  if (value >= 50) {
    return <Typography color="primary">{value}%</Typography>
  }
  if (value >= 25) {
    return <Typography color="warning.main">{value}%</Typography>
  }
  return <Typography color="error">{value}%</Typography>
}

const formatDateTime = ts => {
  if (!ts) return i18n`Never`
  return getShortDateDiff(ts)
}
const getStatusTooltipText = (online, lastCommunication, deepSleep) => {
  if (deepSleep) return i18n`Deep sleep since: ${formattedDate(deepSleep, 'SHORT_DATETIME')}`
  if (lastCommunication && !online) return i18n`Offline since: ${formattedDate(lastCommunication, 'SHORT_DATETIME')}`
  return null
}

const devicePicker = pick([
  'id',
  'aqualab',
  'battery',
  'assigned',
  'calibrationType',
  'createdOn',
  'deviceName',
  'ipAddress',
  'provisionedOn',
  'serialNumber',
])
// Convert device information into useful data fields for the table
export const getDeviceDataFactory = memoizeOne(({
  models,
  rooms = EMPTY_OBJECT,
  zones = EMPTY_OBJECT
}, modelVariants = EMPTY_OBJECT, battery9VData = EMPTY_OBJECT) => device => {
  if (!device || !models) return null
  const {
    battery,
    deepSleep,
    lastCommunication,
    online,
    room: roomId,
    rssi,
    linkQuality,
    sensor,
    zone: zoneId,
  } = device

  const modelKey = device.sensor?.modelKey ?? device.modelKey
  let model = modelKey in models ? models[modelKey] : null
  if (device.sensor && device.sensor.modelVariant) {
    const { [device.sensor.modelVariant]: variant } = modelVariants
    model = variant ?? model
  } else if (device.modelVariant) {
    const { [device.modelVariant]: variant } = modelVariants
    model = variant ?? model
  }
  const signal = linkQuality ?? rssiToSignal(rssi)
  const { battery9V, battery9VColor, Icon: Battery9VIcon } = getBattery9V(battery9VData[device.id])
  const data = {
    ...devicePicker(device),
    // derived data
    icons: {
      BatteryIcon: getBattery(battery).Icon,
      Battery9VIcon,
      SignalIcon: getSignalIcon(signal).Icon,
      StatusIcon: getStatus(online, deepSleep),
    },
    battery9V,
    battery9VColor,
    isGateway: model?.gateway ?? false,
    lastComm: formatDateTime(lastCommunication),
    model: model?.name ?? '',
    room: roomId in rooms ? rooms[roomId] : null,
    signal,
    statusTooltip: getStatusTooltipText(online, lastCommunication, deepSleep),
    zone: zoneId in zones ? zones[zoneId] : null,
  }

  if (sensor) {
    data.sensorLastComm = formatDateTime(sensor.lastCommunication)
    data.sensorStatusTooltip = getStatusTooltipText(sensor.online, sensor.lastCommunication)
    data.icons.SensorStatusIcon = getStatus(sensor.online)
    if (sensor.calibrationType !== data.calibrationType) {
      data.calibrationType = sensor.calibrationType
    }
  }

  return data
})

export const getDevicePendingStatus = (devicesProvisioningStatus, device = false) => {
  if (device?.serialNumber && devicesProvisioningStatus) {
    return devicesProvisioningStatus.find(d => d?.serialNumber === device?.serialNumber)?.status === 'PENDING'
  }
  return false
}

export const usePendingDevices = () => {
  const { devicesProvisioningStatus } = useConnect('selectDevicesProvisioningStatus')
  return useMemo(() => devicesProvisioningStatus.some(({ status }) => status === 'PENDING'), [devicesProvisioningStatus])
}

export const DEVICE_STATES = {
  ALL: '',
  INSTALLED: 'installed',
  NOT_INSTALLED: 'notInstalled',
  LOW_BATTERY: 'lowBattery',
  LOW_SIGNAL: 'lowSignal',
  ASLEEP: 'asleep',
  OFFLINE: 'offline',
  RECENTLY_PROVISIONED: 'recentlyProvisioned',
}
export const stateFilters = [
  { key: DEVICE_STATES.ALL, label: i18n`All Devices` },
  { key: DEVICE_STATES.INSTALLED, label: i18n`Assigned` },
  { key: DEVICE_STATES.NOT_INSTALLED, label: i18n`Unassigned` },
  { key: DEVICE_STATES.LOW_BATTERY, label: i18n`Low Battery` },
  { key: DEVICE_STATES.LOW_SIGNAL, label: i18n`Low Signal` },
  { key: DEVICE_STATES.ASLEEP, label: i18n`Asleep` },
  { key: DEVICE_STATES.OFFLINE, label: i18n`Offline` },
  { key: DEVICE_STATES.RECENTLY_PROVISIONED, label: i18n`Recently Activated` },
]

export const useNewDevices = devices => {
  const {
    devicesProvisioningStatus,
    provisioningStatusRaw
  } = useConnect(
    'selectProvisioningStatusRaw',
    'selectDevicesProvisioningStatus'
  )
  const { pollingStart } = provisioningStatusRaw
  const pendingDevices = useMemo(() => devicesProvisioningStatus.filter(d => d.status === 'PENDING'), [devicesProvisioningStatus])
  const newDevices = useMemo(() => devices.reduce((acc, d) => {
    const justProvisioned = getTimeDiffHours(getDateTime(pollingStart), getDateTime(d.provisionedOn)) > 0
    const wasProvisioned = getTimeDiffHours(getDateTime(pollingStart), getDateTime(d.provisionedOn)) < 24
    const gatewayIsPending = pendingDevices.find(pendingDevice => d.serialNumber === pendingDevice.serialNumber)
    if (pollingStart && justProvisioned && wasProvisioned && !d?.assigned && !gatewayIsPending) {
      return acc + 1
    }
    return acc
  }, 0), [devices, pollingStart, pendingDevices])
  return [pendingDevices, newDevices]
}
